import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import APIClient from '../api/axios';
import { useAuth } from '../context/AuthContext';
import {
  FiPlus, FiAlertCircle, FiTool, FiCheckCircle,
  FiClock, FiMapPin, FiCalendar, FiUser, FiChevronDown, FiArrowRight,
  FiEdit2, FiTrash2, FiX, FiImage, FiMail,
} from 'react-icons/fi';
import './SiteExecution.css';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', dot: '#94a3b8' },
  in_progress:  { label: 'In Progress', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6' },
  completed:    { label: 'Completed',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e' },
  on_hold:      { label: 'On Hold',     color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
};

const PROJECT_STATUS_MAP = {
  planning: 'not_started',
  in_progress: 'in_progress',
  review: 'in_progress',
  completed: 'completed',
  on_hold: 'on_hold',
};

const ISSUE_STATUS = {
  open:        { label: 'Open',        color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  in_progress: { label: 'In Progress', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  resolved:    { label: 'Resolved',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
};


function StatusPill({ status, config }) {
  const cfg = config[status] || config.not_started || Object.values(config)[0];
  return (
    <span className="se-pill" style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
      <span className="se-pill-dot" style={{ background: cfg.dot || cfg.color }} />
      {cfg.label}
    </span>
  );
}

function StatusDropdown({ value, onChange, config }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const cfg = config[value] || Object.values(config)[0];
  return (
    <div className="se-status-dd" ref={ref}>
      <button
        className="se-status-btn"
        style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
        onClick={() => setOpen(v => !v)}
      >
        <span className="se-pill-dot" style={{ background: cfg.dot || cfg.color }} />
        {cfg.label}
        <FiChevronDown size={13} />
      </button>
      {open && (
        <div className="se-dd-menu">
          {Object.entries(config).map(([k, v]) => (
            <button key={k} className={`se-dd-item ${k === value ? 'active' : ''}`}
              style={{ color: v.color }}
              onClick={() => { onChange(k); setOpen(false); }}>
              <span className="se-pill-dot" style={{ background: v.dot || v.color }} />
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SiteExecution() {
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const isSupervisor = currentUser?.role?.name === 'Supervisor';
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [workItems, setWorkItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [editWork, setEditWork] = useState(null);

  useEffect(() => {
    const pid = new URLSearchParams(location.search).get('projectId');
    APIClient.get('/projects')
      .then(r => {
        let allProjects = r.data;
        // Supervisors only see projects they are assigned to
        const uid = currentUser?._id || currentUser?.id;
        if (isSupervisor && uid) {
          allProjects = allProjects.filter(p =>
            (p.team || []).some(t => (t.user?._id || t.user) === uid)
          );
        }
        setProjects(allProjects);
        const target = pid ? allProjects.find(p => p._id === pid) : null;
        setSelectedProject(target || allProjects[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProject) return;
    loadWorkItems();
    loadIssues();
    loadPhotos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedDate]);

  function loadWorkItems() {
    APIClient.get('/site-execution/work-items', {
      params: { project: selectedProject._id, date: selectedDate },
    }).then(r => setWorkItems(r.data))
      .catch(() => setWorkItems([]));
  }

  function loadIssues() {
    APIClient.get('/site-execution/issues', {
      params: { project: selectedProject._id },
    }).then(r => setIssues(r.data))
      .catch(() => setIssues([]));
  }

  function loadPhotos() {
    APIClient.get('/site-execution/photos', {
      params: { project: selectedProject._id },
    }).then(r => setPhotos(r.data))
      .catch(() => setPhotos([]));
  }

  async function updateWorkStatus(id, status) {
    try {
      await APIClient.put(`/site-execution/work-items/${id}`, { status });
      setWorkItems(prev => prev.map(w => w._id === id ? { ...w, status } : w));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  }

  async function deleteWorkItem(id) {
    if (!window.confirm('Delete this work item?')) return;
    try {
      await APIClient.delete(`/site-execution/work-items/${id}`);
      setWorkItems(prev => prev.filter(w => w._id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  }

  const stats = {
    not_started: workItems.filter(w => w.status === 'not_started').length,
    in_progress: workItems.filter(w => w.status === 'in_progress').length,
    completed:   workItems.filter(w => w.status === 'completed').length,
  };

  const fmt = iso => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';

  const WORK_ICONS = {
    carpentry: '🪚', electrical: '⚡', plumbing: '🔧', painting: '🎨',
    flooring: '🏗️', ceiling: '🏠', furniture: '🛋️', general: '📋',
  };

  return (
    <Layout>
      {loading && <PageLoader text="Loading site execution..." />}
    <div className="se-root">
      {/* ── Top bar with project selector + date ── */}
      <div className="se-topbar">
        <div className="se-topbar-controls">
          <div className="se-proj-select-wrap">
            <ProjectSearchSelect
              projects={projects}
              value={selectedProject}
              onChange={setSelectedProject}
              objectMode={true}
              label="Select Project"
              placeholder="Choose project…"
            />
          </div>
          <div className="se-date-wrap">
            <label>Select Date</label>
            <div className="se-date-box">
              <FiCalendar size={15} />
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Project Info Card ── */}
      {selectedProject && (
        <div className="se-proj-card">
          <div className="se-proj-img-wrap">
            <div className="se-proj-img-placeholder">
              <FiImage size={32} color="#94a3b8" />
            </div>
          </div>
          <div className="se-proj-info">
            <div className="se-proj-header-row">
              <h3 className="se-proj-name">
                {selectedProject.projectId ? `${selectedProject.projectId} – ` : ''}{selectedProject.title}
              </h3>
              <StatusPill status={PROJECT_STATUS_MAP[selectedProject.status] || 'not_started'} config={STATUS_CONFIG} />
            </div>
            <div className="se-proj-meta">
              <span><FiUser size={13} /> <b>Customer</b> {selectedProject.client?.name || '—'}</span>
              <span><FiUser size={13} /> <b>Supervisor</b> {
                selectedProject.team?.find(t => t.role?.toLowerCase().includes('supervisor'))?.user?.name
                || selectedProject.supervisor
                || '—'
              }</span>
              <span><FiCalendar size={13} /> <b>Start Date</b> {fmt(selectedProject.startDate)}</span>
              <span><FiCalendar size={13} /> <b>Expected Completion</b> {fmt(selectedProject.endDate)}</span>
              <span><FiMapPin size={13} /> <b>Location</b> {selectedProject.location || selectedProject.address || '—'}</span>
            </div>
          </div>
          <div className="se-work-stats">
            <h4>Work Status Overview</h4>
            <div className="se-stat-cards">
              <div className="se-stat-card se-stat-gray">
                <span className="se-stat-num">{stats.not_started}</span>
                <span className="se-stat-lbl">Not Started</span>
                <span className="se-stat-sub">Tasks</span>
              </div>
              <div className="se-stat-card se-stat-blue">
                <span className="se-stat-num">{stats.in_progress}</span>
                <span className="se-stat-lbl">In Progress</span>
                <span className="se-stat-sub">Tasks</span>
              </div>
              <div className="se-stat-card se-stat-green">
                <span className="se-stat-num">{stats.completed}</span>
                <span className="se-stat-lbl">Completed</span>
                <span className="se-stat-sub">Tasks</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Main 2-col grid ── */}
      <div className="se-main-grid">
        {/* LEFT: Daily Work + Work Status Guide */}
        <div className="se-left-col">
          {/* Daily Work / Activities */}
          <div className="se-card">
            <div className="se-card-header">
              <h3>Daily Work / Activities</h3>
              <button className="se-btn-primary" onClick={() => { setEditWork(null); setShowWorkModal(true); }}>
                <FiPlus size={14} /> Add Work Update
              </button>
            </div>
            <div className="se-work-list">
              {loading && <div className="se-empty">Loading…</div>}
              {!loading && workItems.length === 0 && (
                <div className="se-empty">No work items for this date. Click "+ Add Work Update" to get started.</div>
              )}
              {workItems.map(w => (
                <div key={w._id} className="se-work-item">
                  <div className={`se-work-dot se-dot-${w.status}`} />
                  <div className="se-work-icon-box">
                    <span>{WORK_ICONS[w.category] || '📋'}</span>
                  </div>
                  <div className="se-work-content">
                    <div className="se-work-title-row">
                      <span className="se-work-title">{w.title}</span>
                      <div className="se-work-actions">
                        <button className="se-icon-btn" onClick={() => { setEditWork(w); setShowWorkModal(true); }}><FiEdit2 size={13} /></button>
                        <button className="se-icon-btn se-icon-del" onClick={() => deleteWorkItem(w._id)}><FiTrash2 size={13} /></button>
                      </div>
                    </div>
                    <p className="se-work-desc">{w.description}</p>
                    <div className="se-work-meta">
                      <span><FiCalendar size={11} /> {fmt(w.date)}</span>
                      {w.time && <span><FiClock size={11} /> {w.time}</span>}
                      {w.assignedTo && <span><FiUser size={11} /> {w.assignedTo}</span>}
                    </div>
                    <div className="se-work-photos">
                      {(w.photos || []).slice(0, 3).map((ph, i) => (
                        <div key={i} className="se-thumb">
                          <img src={ph.startsWith('http') ? ph : `http://localhost:9119${ph}`} alt="site" />
                        </div>
                      ))}
                      {(w.photos || []).length > 3 && (
                        <div className="se-thumb se-thumb-more">+{w.photos.length - 3}</div>
                      )}
                    </div>
                  </div>
                  <div className="se-work-status-col">
                    <span className="se-status-label">Status</span>
                    <StatusDropdown value={w.status} onChange={s => updateWorkStatus(w._id, s)} config={STATUS_CONFIG} />
                  </div>
                </div>
              ))}
            </div>
            {workItems.length > 0 && (
              <button className="se-view-all-btn">View All Work Updates <FiArrowRight size={13} /></button>
            )}
          </div>

          {/* Work Status Guide */}
          <div className="se-card se-guide-card">
            <h4 className="se-guide-title">Work Status Guide</h4>
            <div className="se-guide-grid">
              <div className="se-guide-item">
                <div className="se-guide-icon se-guide-gray"><FiClock size={16} /></div>
                <div>
                  <p className="se-guide-name">Not Started</p>
                  <p className="se-guide-desc">Work is not yet started.</p>
                </div>
              </div>
              <div className="se-guide-item">
                <div className="se-guide-icon se-guide-blue"><FiCheckCircle size={16} /></div>
                <div>
                  <p className="se-guide-name se-blue">In Progress</p>
                  <p className="se-guide-desc">Work is currently in progress.</p>
                </div>
              </div>
              <div className="se-guide-item">
                <div className="se-guide-icon se-guide-green"><FiCheckCircle size={16} /></div>
                <div>
                  <p className="se-guide-name se-green">Completed</p>
                  <p className="se-guide-desc">Work has been completed.</p>
                </div>
              </div>
            </div>
            <div className="se-guide-tip">
              <span>🔒</span> Keep site updates, photos and issue reports up to date for better tracking and coordination.
            </div>
          </div>
        </div>

        {/* RIGHT: Supervisor Actions + Recent Photos + Issues */}
        <div className="se-right-col">
          {/* Quick Actions */}
          <div className="se-card">
            <h3 className="se-card-h3">Quick Actions</h3>
            <div className="se-action-grid">
              <button className="se-action-card" onClick={() => { setEditWork(null); setShowWorkModal(true); }}>
                <div className="se-action-icon" style={{ color: '#3b82f6', background: '#eff6ff' }}><FiEdit2 /></div>
                <span className="se-action-label">Add Work Update</span>
              </button>
              <button className="se-action-card" onClick={() => setShowIssueModal(true)}>
                <div className="se-action-icon" style={{ color: '#ef4444', background: '#fef2f2' }}><FiAlertCircle /></div>
                <span className="se-action-label">Report Issue</span>
              </button>
              <button className="se-action-card" onClick={() => {
                if (!workItems.length) return toast.info('No work items for this date');
                document.querySelector('.se-work-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>
                <div className="se-action-icon" style={{ color: '#16a34a', background: '#f0fdf4' }}><FiCheckCircle /></div>
                <span className="se-action-label">Update Status</span>
              </button>
              <button className="se-action-card" onClick={() => {
                const pending = workItems.filter(w => w.status === 'not_started');
                toast.info(pending.length ? `${pending.length} task(s) not yet started` : 'All tasks started!');
              }}>
                <div className="se-action-icon" style={{ color: '#f59e0b', background: '#fffbeb' }}><FiTool /></div>
                <span className="se-action-label">Check Pending</span>
              </button>
            </div>
          </div>

          {/* Project Team */}
          {(selectedProject?.team?.length > 0) && (
            <div className="se-card">
              <h3 className="se-card-h3">Project Team</h3>
              <div className="se-team-list">
                {selectedProject.team.map((t, i) => {
                  const name = t.user?.name || '—';
                  const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
                  const colors = ['#3b82f6','#16a34a','#7c3aed','#f59e0b','#ef4444'];
                  return (
                    <div key={i} className="se-team-row">
                      <div className="se-team-avatar" style={{ background: colors[i % colors.length] }}>{initials}</div>
                      <div className="se-team-info">
                        <span className="se-team-name">{name}</span>
                        <span className="se-team-role">{t.role || 'Team Member'}</span>
                      </div>
                      {t.user?.email && (
                        <a href={`mailto:${t.user.email}`} className="se-team-contact" title={t.user.email}>
                          <FiMail size={13}/>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Site Photos */}
          <div className="se-card">
            <div className="se-card-header">
              <h3>Recent Site Photos</h3>
              <button className="se-link-btn">View All</button>
            </div>
            <div className="se-photos-grid">
              {photos.length === 0 && (
                <div className="se-empty">No photos uploaded yet.</div>
              )}
              {photos.slice(0, 4).map((ph, i) => (
                <div key={i} className="se-photo-item">
                  <img src={ph.url?.startsWith('http') ? ph.url : `http://localhost:9119${ph.url}`} alt="site" />
                  <div className="se-photo-meta">
                    <span>{fmt(ph.uploadedAt)}</span>
                    <span>{fmtTime(ph.uploadedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Issues Reported */}
          <div className="se-card">
            <div className="se-card-header">
              <h3>Issues Reported</h3>
              <button className="se-link-btn" onClick={() => setShowIssueModal(true)}>View All</button>
            </div>
            <div className="se-table-wrap">
            <table className="se-issues-table">
              <thead>
                <tr>
                  <th>Issue Title</th>
                  <th>Reported On</th>
                  <th>Reported By</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {issues.length === 0 && (
                  <tr><td colSpan={4} className="se-empty">No issues reported.</td></tr>
                )}
                {issues.slice(0, 5).map(iss => (
                  <tr key={iss._id}>
                    <td className="se-issue-title">{iss.title}</td>
                    <td>{fmt(iss.reportedAt || iss.createdAt)}</td>
                    <td>{iss.reportedBy?.name || iss.reportedBy || '—'}</td>
                    <td>
                      <span className="se-issue-badge"
                        style={{
                          color: ISSUE_STATUS[iss.status]?.color || '#64748b',
                          background: ISSUE_STATUS[iss.status]?.bg || '#f1f5f9',
                          border: `1px solid ${ISSUE_STATUS[iss.status]?.border || '#e2e8f0'}`,
                        }}>
                        {ISSUE_STATUS[iss.status]?.label || iss.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {issues.length > 0 && (
              <button className="se-view-all-btn">View All Issues <FiArrowRight size={13} /></button>
            )}
          </div>
        </div>
      </div>

      {/* ── Add / Edit Work Item Modal ── */}
      {showWorkModal && (
        <WorkModal
          work={editWork}
          projectId={selectedProject?._id}
          defaultDate={selectedDate}
          teamUserIds={(selectedProject?.team || []).map(t => t.user?._id || t.user).filter(Boolean)}
          onClose={() => { setShowWorkModal(false); setEditWork(null); }}
          onSaved={() => { loadWorkItems(); setShowWorkModal(false); setEditWork(null); }}
        />
      )}

      {/* ── Report Issue Modal ── */}
      {showIssueModal && (
        <IssueModal
          projectId={selectedProject?._id}
          onClose={() => setShowIssueModal(false)}
          onSaved={() => { loadIssues(); setShowIssueModal(false); }}
        />
      )}
    </div>
    </Layout>
  );
}

/* ─── Work Item Modal ─── */
function WorkModal({ work, projectId, defaultDate, teamUserIds = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    title: work?.title || '',
    description: work?.description || '',
    category: work?.category || 'general',
    status: work?.status || 'not_started',
    date: work?.date ? work.date.slice(0, 10) : (defaultDate || new Date().toISOString().slice(0, 10)),
  });
  const [customTitle, setCustomTitle] = useState('');
  const [projectTasks, setProjectTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!projectId || !form.date) return;
    APIClient.get('/tasks', { params: { project: projectId, date: form.date } })
      .then(r => {
        const all = r.data || [];
        // Show only tasks assigned to project team members (or unassigned to this project)
        const filtered = teamUserIds.length > 0
          ? all.filter(t => !t.assignedTo || teamUserIds.includes(t.assignedTo?._id || t.assignedTo))
          : all;
        setProjectTasks(filtered);
      })
      .catch(() => {});
  }, [projectId, form.date]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    const finalTitle = form.title === '__custom__' ? customTitle.trim() : form.title.trim();
    if (!finalTitle) return toast.error('Title is required');
    setSaving(true);
    try {
      const now = new Date();
      const payload = {
        ...form,
        title: finalTitle,
        project: projectId,
        time: now.toTimeString().slice(0, 5),
      };
      if (work) {
        await APIClient.put(`/site-execution/work-items/${work._id}`, payload);
        toast.success('Work item updated');
      } else {
        await APIClient.post('/site-execution/work-items', payload);
        toast.success('Work item added');
      }
      onSaved();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="se-modal-overlay">
      <div className="se-modal" onClick={e => e.stopPropagation()}>
        <div className="se-modal-header">
          <h3>{work ? 'Edit Work Item' : 'Add Work Update'}</h3>
          <button className="se-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="se-modal-body">
          <div className="se-form-row">
            <div className="se-form-group se-fg-full">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => { set('date', e.target.value); set('title', ''); }} />
            </div>
          </div>
          <div className="se-form-row">
            <div className="se-form-group se-fg-full">
              <label>Title *</label>
              {projectTasks.length > 0 ? (
                <select value={form.title} onChange={e => set('title', e.target.value)}>
                  <option value="">— Select from Project Tasks —</option>
                  {projectTasks.map(t => (
                    <option key={t._id} value={t.title}>{t.title}</option>
                  ))}
                  <option value="__custom__">+ Enter custom title…</option>
                </select>
              ) : (
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Kitchen – Modular Work" />
              )}
              {form.title === '__custom__' && (
                <input
                  style={{ marginTop: '0.4rem' }}
                  autoFocus
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                  placeholder="Enter custom title"
                />
              )}
            </div>
          </div>
          <div className="se-form-row">
            <div className="se-form-group">
              <label>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                {['general','carpentry','electrical','plumbing','painting','flooring','ceiling','furniture'].map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="se-form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div className="se-form-row">
            <div className="se-form-group se-fg-full">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the work being done…" />
            </div>
          </div>
        </div>
        <div className="se-modal-footer">
          <button className="se-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="se-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : work ? 'Update' : 'Add Work Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Issue Modal ─── */
function IssueModal({ projectId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', status: 'open', reportedBy: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      await APIClient.post('/site-execution/issues', { ...form, project: projectId });
      toast.success('Issue reported');
      onSaved();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="se-modal-overlay">
      <div className="se-modal" onClick={e => e.stopPropagation()}>
        <div className="se-modal-header">
          <h3>Report Issue</h3>
          <button className="se-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="se-modal-body">
          <div className="se-form-row">
            <div className="se-form-group se-fg-full">
              <label>Issue Title *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Material delay – Plywood" />
            </div>
          </div>
          <div className="se-form-row">
            <div className="se-form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(ISSUE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="se-form-group">
              <label>Reported By</label>
              <input value={form.reportedBy} onChange={e => set('reportedBy', e.target.value)} placeholder="Name" />
            </div>
          </div>
          <div className="se-form-row">
            <div className="se-form-group se-fg-full">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the issue…" />
            </div>
          </div>
        </div>
        <div className="se-modal-footer">
          <button className="se-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="se-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Report Issue'}</button>
        </div>
      </div>
    </div>
  );
}
